import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { AmountInput, CurrenciesUSD } from '@column/column-ui-kit';

import { InfoForm } from '../Transfers/_components/Layout';
import { ROUTE } from '~/app/routes';
import { BankAccountCard, CopyInput, Details, Loading, RenderFields } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { LoanRepository, LoanSale } from '~/repositories';
import { AccountNumber, BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { LoanPageHeader } from './_components/LoanPageHeader';

interface Params {
  id: string;
}

export const PageLoansSale: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();
  const { currentPlatform } = useSessionStore();

  const {
    data: sale,
    isLoading: isSaleLoading,
    error: saleError,
  } = useQuery({
    queryKey: ['loan-sale', id, currentPlatform?.id],
    queryFn: () => LoanRepository.getSale(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (saleError) {
      const errorMessage = saleError instanceof Error ? saleError.message : 'Failed to load loan sale';
      addDangerNotification({ content: errorMessage });
      navigate(ROUTE.LOANS);
    }
  }, [saleError, addDangerNotification, navigate]);

  const formMethods = useForm<LoanSale>({
    defaultValues: {} as LoanSale,
  });

  useEffect(() => {
    if (sale) {
      formMethods.reset(sale);
    }
  }, [sale, formMethods]);

  const amountInCents = sale?.amount ? parseInt(sale.amount, 10) : undefined;

  const { data: accountNumber, isLoading: isAccountNumberLoading } = useQuery<AccountNumber | null>({
    queryKey: ['account-number', sale?.purchaseFundingAccountNumberId, currentPlatform?.id],
    queryFn: async () => {
      if (!sale?.purchaseFundingAccountNumberId) return null;
      return BankAccountRepository.getAccountNumber({
        id: sale.purchaseFundingAccountNumberId,
        platformId: currentPlatform?.id,
      });
    },
    enabled: !!sale?.purchaseFundingAccountNumberId && !!currentPlatform?.id,
    gcTime: 30000,
    staleTime: 10000,
  });

  return (
    <>
      <Loading isLoading={isSaleLoading} fullHeight>
        {sale && (
          <>
            <LoanPageHeader
              amount={sale.amount}
              paymentDate={sale.soldAt || sale.createdAt}
              loanId={sale.id}
              loanType="sale"
              currencyCode={sale.currencyCode}
              status={sale.soldAt ? 'sold' : undefined}
            />
            <FormProvider {...formMethods}>
              <InfoForm autoComplete="off">
                {RenderFields<LoanSale>({
                  sections: [
                    {
                      headline: 'Information',
                      fields: [
                        {
                          id: 'purchaseFundingAccountNumberId',
                          label: 'Purchase Funding Account',
                          children: () => {
                            if (!sale?.purchaseFundingAccountNumberId) {
                              return <CopyInput value="-" />;
                            }
                            if (isAccountNumberLoading || !accountNumber) {
                              return <CopyInput value="Loading..." />;
                            }
                            if (accountNumber.bankAccountId) {
                              return <BankAccountCard bankAccountId={accountNumber.bankAccountId} />;
                            }
                            return <CopyInput value={sale.purchaseFundingAccountNumberId} />;
                          },
                        },
                        {
                          id: 'amount',
                          label: 'Amount',
                          children: () => {
                            return (
                              <AmountInput
                                value={amountInCents}
                                currencyCode={sale.currencyCode || 'USD'}
                                currencyList={CurrenciesUSD}
                                readOnly
                                fixedCurrencyCode={sale.currencyCode || 'USD'}
                              />
                            );
                          },
                        },
                        {
                          id: 'description',
                          label: 'Description',
                          children: ({ value }) => {
                            return <CopyInput value={String(value || '-')} />;
                          },
                        },
                        {
                          id: 'soldAt',
                          label: 'Sold At',
                          children: ({ value }) => {
                            return <CopyInput value={String(value || '-')} />;
                          },
                        },
                      ],
                    },
                  ],
                })}
                <Details
                  label="Loan Sale Details"
                  formattedData={sale as unknown as Record<string, unknown>}
                  rawData={sale as unknown as Record<string, unknown> | undefined}
                />
              </InfoForm>
            </FormProvider>
          </>
        )}
      </Loading>
    </>
  );
};
